// ============================================
// Sổ bằng chứng bán hàng 18+ — TanStack Query
// ============================================
// GET /api/v1/audit/restricted-sales/        — danh sách, envelope {data, meta}
// GET /api/v1/audit/restricted-sales/export/ — file CSV
//
// Quyền: STORE_ADMIN trở lên. Chỉ thấy cửa hàng mình được gán.

import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  RESTRICTED_SALES_MAX_RANGE_DAYS,
  restrictedSalesRangeDays,
  type RestrictedSalesParams,
} from '@conai/shared-types';
import { useAuditLogsService } from '../providers/ApiProvider';
import { queryKeys } from '../config/query-client';

/** Bỏ key rỗng để không gửi `?order_code=` làm backend lọc theo chuỗi trống. */
function cleanParams(params: RestrictedSalesParams): RestrictedSalesParams {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') out[key] = value;
  }
  return out as RestrictedSalesParams;
}

/**
 * Khoảng ngày có vượt giới hạn của backend không.
 *
 * Chặn ở client để không tốn một vòng gọi rồi nhận 400 — và để câu thông báo nói
 * đúng vấn đề thay vì "Bad request".
 */
export function restrictedSalesRangeError(
  params: RestrictedSalesParams
): string | null {
  if (params.start_date && params.end_date && params.end_date < params.start_date) {
    return 'rsRangeReversed';
  }
  const days = restrictedSalesRangeDays(params.start_date, params.end_date);
  if (days != null && days > RESTRICTED_SALES_MAX_RANGE_DAYS) return 'rsRangeTooLong';
  return null;
}

export function useRestrictedSales(params: RestrictedSalesParams) {
  const auditLogs = useAuditLogsService();
  const cleaned = cleanParams(params);
  // Khoảng ngày sai thì đừng gọi API — chỉ để UI hiện lỗi.
  const isValid = restrictedSalesRangeError(cleaned) === null;

  return useQuery({
    queryKey: queryKeys.restrictedSales.list(cleaned as object),
    queryFn: () => auditLogs.listRestrictedSales(cleaned),
    enabled: isValid,
    // Sổ này là dữ liệu lịch sử, không đổi sau lưng người xem — không cần refetch
    // mỗi lần focus lại cửa sổ.
    staleTime: 60_000,
  });
}

/**
 * Tải sổ dạng CSV.
 *
 * Tự tải blob rồi tạo link thay vì mở tab mới: endpoint cần header
 * Authorization, mà `window.open` không gửi kèm được.
 */
export function useExportRestrictedSales() {
  const auditLogs = useAuditLogsService();
  const [isExporting, setIsExporting] = useState(false);

  const exportCsv = useCallback(
    async (params: RestrictedSalesParams) => {
      setIsExporting(true);
      let objectUrl: string | null = null;
      try {
        const blob = await auditLogs.exportRestrictedSales(cleanParams(params));
        const stamp = new Date().toISOString().slice(0, 10);
        objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = `restricted_sales_${stamp}.csv`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Export failed');
      } finally {
        // Thu hồi trong finally: rò URL này là giữ nguyên cả file trong bộ nhớ
        // tab cho tới khi đóng, và sổ 400 ngày không phải file nhỏ.
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        setIsExporting(false);
      }
    },
    [auditLogs]
  );

  return { exportCsv, isExporting };
}
